/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';

export interface PreviewMailDialogData {
    title: string;
    html: string;
}

const PHONE_WIDTH = '390px';
const LAPTOP_WIDTH = '80vw';

/**
 * Renders a full mail HTML document (doctype/head/style included) in an iframe rather than an
 * innerHtml div - a div strips the <head>/<style>, so the mail's own CSS never applied and the
 * preview didn't look like the real layout. The toggle resizes the whole dialog (via
 * dialogRef.updateSize) between a laptop width and a real phone-width viewport, so the preview
 * frames the mail the way the target device would; the iframe just fills whatever width the
 * dialog currently has. Callers don't need to pass a width - the component sets its own on init.
 */
@Component({
    selector: 'app-preview-mail-dialog',
    imports: [DialogBannerComponent, MatButtonModule, MatIconModule, MatTooltipModule],
    template: `
        <app-dialog-banner [text]="data.title" [dialogRef]="dialogRef"></app-dialog-banner>
        <div style="display: flex; justify-content: flex-end; padding: 4px 12px;">
            <button mat-icon-button [matTooltip]="isPhone ? 'Show laptop width' : 'Show phone width'" (click)="toggleWidth()">
                <mat-icon>{{ isPhone ? 'laptop' : 'smartphone' }}</mat-icon>
            </button>
        </div>
        <div style="overflow-x: auto; background: #e0e0e0; padding: 12px 0;">
            <iframe [srcdoc]="safeHtml"
                style="width: 100%; height: 70vh; border: none; display: block; margin: 0 auto; background: white;"></iframe>
        </div>
    `
})
export class PreviewMailDialogComponent {
    dialogRef = inject<MatDialogRef<PreviewMailDialogComponent>>(MatDialogRef);
    data = inject<PreviewMailDialogData>(MAT_DIALOG_DATA);
    private sanitizer = inject(DomSanitizer);

    safeHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(this.data.html);
    isPhone = false;

    constructor() {
        this.dialogRef.updateSize(LAPTOP_WIDTH);
    }

    toggleWidth() {
        this.isPhone = !this.isPhone;
        this.dialogRef.updateSize(this.isPhone ? PHONE_WIDTH : LAPTOP_WIDTH);
    }
}
